import { booleanAttribute } from "#ce/utils/props";

// `checked` and `indeterminate` are absent on purpose and are documented in the story: the element
// owns them as live properties, which declared props would shadow with accessors onto the prop
// store. The `checked` attribute names the state a reset restores, as on a native checkbox.
export const props = Object.freeze({
  /**
   * Value submitted with the form while the box is checked. An unchecked box submits nothing.
   *
   * @type {String}
   * @default "on"
   */
  value: {
    type: String,
    default: "on",
  },
  /**
   * Text rendered next to the box. Clicking it toggles the checked state. The `label` slot takes precedence.
   *
   * @type {String}
   * @default undefined
   */
  label: {
    type: String,
    default: undefined,
  },
  /**
   * Render the label before the box instead of after.
   *
   * @type {Boolean|String}
   * @default false
   */
  labelPrepend: booleanAttribute(),
  /**
   * Block form submission while the box is unchecked, the way a native `required` checkbox does.
   *
   * @type {Boolean|String}
   * @default false
   */
  required: booleanAttribute(),
  /**
   * Message the browser shows when a required checkbox is submitted unchecked. Names what this
   * particular box commits the visitor to; the generic wording comes from `frontend.forms`.
   *
   * @type {String}
   * @default undefined
   */
  requiredMessage: {
    type: String,
    default: undefined,
  },
  /**
   * Disable user interaction. A disabled checkbox keeps its visual state but ignores clicks.
   *
   * @type {Boolean|String}
   * @default false
   */
  disabled: booleanAttribute(),
  /**
   * Guidance rendered below the row while the box has focus. `persistent-hint` keeps it visible.
   * A `messages` value takes its place; `error-messages` renders separately, below both.
   *
   * @type {String}
   * @default undefined
   */
  hint: {
    type: String,
    default: undefined,
  },
  /**
   * Keep the hint visible whether or not the box has focus.
   *
   * @type {Boolean|String}
   * @default false
   */
  persistentHint: booleanAttribute(),
  /**
   * Message or messages rendered below the row, in place of the hint. A single string, or several
   * as an array assigned through the `messages` property.
   *
   * @type {String|String[]}
   * @default undefined
   */
  messages: {
    type: [String, Array],
    default: undefined,
  },
  /**
   * Error text rendered below the row. Accepts a string or an array of strings.
   *
   * @type {String|Array}
   * @default undefined
   */
  errorMessages: {
    type: [String, Array],
    default: undefined,
  },
});
